import React, { useEffect, useState, useRef, useMemo } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";

export const VersionSelectSettingsForm: React.FC = () => {
  // Store & Service
  const { softwareVersion, setSoftwareVersion } = useJobSetupStore((state) => ({
    softwareVersion: state.CreateJobState.softwareVersion,
    setSoftwareVersion: state.setSoftwareVersion,
  }));
  const injectedListVersionsService = useInjection(ListSoftwareVersionsService);
  const listVersionsService = useRef(injectedListVersionsService).current;
  const softwareVersionRef = useRef(softwareVersion);

  useEffect(() => {
    softwareVersionRef.current = softwareVersion;
  }, [softwareVersion]);

  // UI State
  const [versionsList, setVersionsList] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 450, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 1. Fetch registry versions. Spectronaut image tags are allowed by default.
  useEffect(() => {
    async function fetchVersionList() {
      try {
        const listResponse = await listVersionsService.getVersionsListAsync();
        const registryVersions = listResponse ?? [];
        setVersionsList(registryVersions);

        const defaultVersion = await listVersionsService.getDefaultSpectronautVersionAsync(registryVersions) ?? registryVersions[0];
        const currentSoftwareVersion = softwareVersionRef.current;
        if (currentSoftwareVersion) {
          if (!registryVersions.includes(currentSoftwareVersion)) {
            setSoftwareVersion(defaultVersion ?? "");
          }
          return;
        }

        if (defaultVersion) {
          setSoftwareVersion(defaultVersion);
        }
      } catch (error) {
        console.error(`Error fetching software versions`, error);
      }
    }
    fetchVersionList();
  }, [listVersionsService, setSoftwareVersion]);

  // 2. Grouping Logic
  const groupedData = useMemo(() => {
    const filtered = versionsList.filter(v => v.toLowerCase().includes(searchTerm.toLowerCase()));
    const groups: Record<string, string[]> = {};

    filtered.forEach((v) => {
      const major = v.split(".")[0];
      if (!groups[major]) groups[major] = [];
      groups[major].push(v);
    });

    // Sort versions within each group and sort groups by major version (descending)
    const sortedGroups: Record<string, string[]> = {};
    Object.keys(groups)
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .forEach((major) => {
        sortedGroups[major] = groups[major].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
      });

    return sortedGroups;
  }, [versionsList, searchTerm]);

  // 3. Drag & Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      }
      if (isResizing && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setDropdownSize({
          width: Math.max(300, e.clientX - rect.left),
          height: Math.max(200, e.clientY - rect.top)
        });
      }
    };
    const handleMouseUp = () => { setIsDragging(false); setIsResizing(false); };
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  useEffect(() => {
    if (dropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({ x: rect.left, y: rect.bottom + 5 });
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [dropdownOpen]);

  return (
    <fieldset className="k-form-fieldset" style={{ marginTop: "10px" }}>
      <legend className="k-form-legend">
        <strong>Spectronaut Version</strong>
      </legend>

      {/* Selector Box */}
      <div
        ref={triggerRef}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        style={{
          border: "1px solid #ccc",
          borderRadius: "3px",
          padding: "8px 12px",
          backgroundColor: "white",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <span>{softwareVersion || "Select version..."}</span>
        <span style={{ fontSize: "10px", color: "#666" }}>{dropdownOpen ? "▲" : "▼"}</span>
      </div>

      {dropdownOpen && (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setDropdownOpen(false)} />
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* Header */}
            <div
              style={{ padding: "8px 12px", backgroundColor: "#f5f5f5", cursor: "move", display: "flex", justifyContent: "space-between" }}
              onMouseDown={(e) => {
                setIsDragging(true);
                const rect = dropdownRef.current?.getBoundingClientRect();
                if (rect) setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13px" }}>Versions</span>
              <button onClick={() => setDropdownOpen(false)} style={{ border: "none", background: "none", cursor: "pointer" }}>×</button>
            </div>

            {/* Search */}
            <div style={{ padding: "10px" }}>
              <input
                ref={searchInputRef}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "3px" }}
                placeholder="Search version..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* List with Grouping */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {versionsList.length === 0 && (
                <div style={{ padding: "12px", color: "#666", fontSize: "13px" }}>
                  No Spectronaut image tags are available from the registry.
                </div>
              )}
              {Object.keys(groupedData).map((major) => (
                <div key={major}>
                  <div style={{ padding: "4px 12px", backgroundColor: "#fafafa", fontSize: "11px", fontWeight: "bold", color: "#888", borderBottom: "1px solid #eee" }}>
                    VERSION {major}
                  </div>
                  {groupedData[major].map((v) => (
                    <div
                      key={v}
                      style={{
                        padding: "8px 24px",
                        cursor: "pointer",
                        backgroundColor: softwareVersion === v ? "#e3f2fd" : "transparent"
                      }}
                      onClick={() => {
                        setSoftwareVersion(v);
                        setDropdownOpen(false);
                      }}
                    >
                      <span style={{ fontSize: "13px" }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Resize Handle */}
            <div
              style={{ position: "absolute", bottom: 0, right: 0, width: "15px", height: "15px", cursor: "se-resize", background: "linear-gradient(135deg, transparent 50%, #999 50%)" }}
              onMouseDown={(e) => { e.stopPropagation(); setIsResizing(true); }}
            />
          </div>
        </>
      )}
    </fieldset>
  );
};
